import Icon from "components/icon";

type CardTrend = "up" | "down" | "neutral";

export type IAIDataCardField = {
  label: string;
  value: string;
  trend?: CardTrend;
};

export type IAIDataCardData = {
  __card: true;
  cardType: "portfolio_summary" | "asset_detail" | string;
  title: string;
  fields: Array<IAIDataCardField>;
};

const getSignedTint = (value: string): "positive" | "negative" | "default" => {
  const trimmed = value.trim();
  if (!trimmed.endsWith("%")) return "default";
  if (trimmed.startsWith("+")) return "positive";
  if (trimmed.startsWith("-") || trimmed.startsWith("\u2212")) return "negative";
  return "default";
};

const tintClass = (tint: "positive" | "negative" | "default"): string => {
  if (tint === "positive") return "text-[#0e9f6e]";
  if (tint === "negative") return "text-red-500";
  return "text-gray-900";
};

const TrendArrow = defineComponent<{ trend: CardTrend; size?: "sm" | "md" }>(
  (props) => {
    return () => {
      if (props.trend === "neutral") return null;
      const isUp = props.trend === "up";
      const px = props.size === "md" ? "0.75rem" : "0.625rem";
      return (
        <span
          class={[
            "inline-flex items-center",
            isUp ? "text-[#0e9f6e]" : "text-red-500",
          ]}
          aria-label={isUp ? "trending up" : "trending down"}
        >
          <Icon
            size={px}
            src={async () => import("assets/img/svg/icons/arrow-up.svg?raw")}
            fill="currentColor"
            style={{
              transform: isUp ? "rotate(0)" : "rotate(180deg)",
            }}
          />
        </span>
      );
    };
  },
  { props: ["trend", "size"] },
);

export const AIDataCard = defineComponent<{
  data: IAIDataCardData;
}>(
  (props) => {
    return () => {
      const [hero, ...rest] = props.data.fields;

      return (
        <div class="w-full max-w-full min-w-0 bg-white rounded-xl border border-gray-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">
          <div class="relative px-5 pt-4 pb-2.5 bg-gradient-to-b from-gray-50/70 to-transparent">
            <span
              aria-hidden="true"
              class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary-500/40 to-transparent"
            />
            <div class="inline-flex items-center gap-2">
              <span class="inline-block w-1.5 h-1.5 rounded-full bg-primary-500 shadow-[0_0_0_3px_rgba(63,131,248,0.12)]" />
              <div class="font-['Inter'] text-[17px] font-semibold tracking-[-0.01em] leading-none text-gray-900">
                {props.data.title}
              </div>
            </div>
          </div>

          {hero ? (
            <div class="px-5 pt-2 pb-4 border-b border-gray-100">
              <div class="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-gray-400 font-['Inter']">
                {hero.label}
              </div>
              <div class="mt-2 flex items-baseline gap-2 flex-wrap">
                <div
                  class={[
                    "text-[30px] font-semibold font-['Inter'] tracking-[-0.025em] leading-none break-words",
                    tintClass(getSignedTint(hero.value)),
                  ]}
                  style={{
                    fontVariantNumeric: "tabular-nums",
                    overflowWrap: "anywhere",
                  }}
                >
                  {hero.value}
                </div>
                {hero.trend ? <TrendArrow trend={hero.trend} size="md" /> : null}
              </div>
            </div>
          ) : null}

          {rest.length > 0 ? (
            <ul class="divide-y divide-gray-100">
              {rest.map((field, i) => {
                const tint = getSignedTint(field.value);
                return (
                  <li
                    key={`card-field-${i}`}
                    class="flex items-baseline justify-between gap-4 px-5 py-3"
                  >
                    <span class="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-gray-500 font-['Inter'] leading-tight">
                      {field.label}
                    </span>
                    <span class="inline-flex items-center gap-1.5 min-w-0 justify-end">
                      <span
                        class={[
                          "text-sm font-medium font-['Inter'] tracking-tight break-words text-right",
                          tintClass(tint),
                        ]}
                        style={{
                          fontVariantNumeric: "tabular-nums",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {field.value}
                      </span>
                      {field.trend ? <TrendArrow trend={field.trend} /> : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      );
    };
  },
  { props: ["data"] },
);
